import { useState, type ReactNode } from 'react'
import { useAssetLibrary } from '@/context/AssetLibraryContext'
import { useToast } from '@/context/ToastContext'
import { assignImagesToRows, type RowKey } from '@/lib/batchImages'
import { Badge, Button, ClearButton, Select } from './ui'

/**
 * ===========================================================================
 * 拖到清单上：一批散文件 → 认得出属于哪一行的，逐行归位
 * ===========================================================================
 *
 * ## 2026-09-30 它从「一张卡」变成了「一层外壳」
 *
 * 上一版它是一张独立的卡片（拖拽区 + 计数 + 未归位清单），摆在清单下面。
 * 用户口径：「参考图……可以提供图片上传」+ 把那张卡圈起来写「删除」。
 *
 * 于是**每行的「参考图」格自己就能上传了**（`BatchTable` 里的
 * `RefUploader wrap="cell"`）—— 那才是这一页加图的主入口。
 *
 * 这个组件**不再是入口，也不再占版面**：它只把「拖到表格上」这件事接住，
 * 外加一条**只在有认不出的文件时**才出现的清单。之所以留着：
 * 20 个产品 80 张图，按行点是 20 次动作；拖着整批丢下来是 1 次
 * —— 而那正是用户最初的抱怨（「csv 最大的问题是不带图片」）。
 *
 * ⚠️ 两个入口（每行的「＋」/ 整批拖进来）写的是**同一份行数据**
 * （都走页面的 `applyImages` → 一次 `patch`），不存在两处各自维护状态的问题。
 * 但拖拽只落在**行以外**的地方 —— 落进某一行的参考图格时，由那一格自己收，
 * 这里必须收不到（见 `BatchImageDrop` 的 drop 分支与 `RefUploader` 的
 * `isCell` 停止冒泡），否则同一张图会被传两遍。
 *
 * ## 三条刻意的规矩（原样保留）
 *
 * 1. **认不出的不猜，列出来让你点**（规则见 `lib/batchImages.ts`）。
 *    归错行的后果是「A 的产品配 B 的参考图」，任务照记 succeeded，
 *    界面上一切正常 —— 只有人眼在一堆缩略图里才发现。宁可多一次点击。
 * 2. **认不出的不传**。先花掉上传再告诉用户「这几张我放不进去」，
 *    等于让他为一批没用的地址买单（图床虽不贵，但磁盘与素材库都脏了）。
 * 3. **串行分批，不是 `Promise.all`**。上游网关对并发有限流，
 *    `upload` 内部也是逐张串的；一次丢 200 张进去只会让重试叠在一起。
 *    分批还有个好处是**进度可数**，否则用户面对一个转了很久的圈，
 *    唯一能做的就是刷新重来。
 */
export function BatchImageDrop({
  rows,
  onAssign,
  disabledHint,
  children,
}: {
  /** 能接收图片的行 —— 只该传**可用的行**（有 error 的行收了图也出不了） */
  rows: RowKey[]
  /** 归位结果：行号 → 这次要追加进去的图片地址 */
  onAssign: (byLine: Map<number, string[]>) => void
  /** 没有可用行时的一句话（说清先去干什么） */
  disabledHint?: string
  /** 投放区里的东西 —— 就是那张产品清单 */
  children: ReactNode
}) {
  const { upload } = useAssetLibrary()
  const toast = useToast()
  const [over, setOver] = useState(false)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  /**
   * 未归位的文件 —— **按原下标记**，不按文件名。
   *
   * 文件名在这个系统里不是标识：后端落盘会改成 uuid，只在响应里**回显**原名。
   * 拿它当字典键，哪天那条回显没了，表现是「选好的行没生效」而不报错。
   */
  const [leftover, setLeftover] = useState<{ index: number; name: string; file: File }[]>([])
  /** 手工指派：原下标 → 行号 */
  const [pick, setPick] = useState<Record<number, number>>({})

  const blocked = rows.length === 0

  /** 上传并归位。`files` 一定要是**原数组**（下标要用来对回行号） */
  const push = async (files: File[]) => {
    if (!files.length) return
    if (blocked) {
      toast.error(disabledHint ?? '先让表格里至少有一行可用，图片才有地方放')
      return
    }
    setBusy(true)
    try {
      // 规则只跑一次：下标 → 行号
      const plan = assignImagesToRows(
        files.map((f) => ({ name: f.name })),
        rows,
      )
      const lineByIndex = new Map(plan.matched.map((m) => [m.index, m.line]))
      setLeftover(plan.unmatched.map((u) => ({ index: u.index, name: u.file.name, file: files[u.index] })))
      setPick({})

      // 只传认得出的
      const sendable = plan.matched.map((m) => files[m.index])
      if (!sendable.length) {
        toast.error('这批文件名里没有能对上任何一行的标识 —— 见下面的清单')
        return
      }
      setProgress({ done: 0, total: sendable.length })
      const byLine = new Map<number, string[]>()
      const CHUNK = 4
      for (let i = 0; i < sendable.length; i += CHUNK) {
        const slice = sendable.slice(i, i + CHUNK)
        // 这一批对应的原下标 —— 上传结果按位置对齐（`upload` 内部是按序 push 的）
        const idx = plan.matched.slice(i, i + CHUNK).map((m) => m.index)
        const added = await upload(slice)
        added.forEach((item, k) => {
          const line = lineByIndex.get(idx[k])
          if (line == null) return
          const list = byLine.get(line) ?? []
          if (!list.includes(item.url)) list.push(item.url)
          byLine.set(line, list)
        })
        setProgress({ done: Math.min(i + CHUNK, sendable.length), total: sendable.length })
      }

      const n = [...byLine.values()].reduce((s, l) => s + l.length, 0)
      if (n) {
        onAssign(byLine)
        toast.success(`已归位 ${n} 张到 ${byLine.size} 行`)
      } else {
        toast.error('图传上去了但没能接到任何一行 —— 请把下面的清单指一下')
      }
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  /** 手工指派：把已经选好行的那些未归位文件传上去 */
  const assignPicked = async () => {
    const todo = leftover.filter((x) => pick[x.index])
    if (!todo.length) return
    setBusy(true)
    try {
      const added = await upload(todo.map((x) => x.file))
      const byLine = new Map<number, string[]>()
      added.forEach((item, k) => {
        const line = pick[todo[k].index]
        if (line == null) return
        const list = byLine.get(line) ?? []
        if (!list.includes(item.url)) list.push(item.url)
        byLine.set(line, list)
      })
      onAssign(byLine)
      setLeftover((list) => list.filter((x) => !pick[x.index]))
      setPick({})
    } finally {
      setBusy(false)
    }
  }

  const pickedCount = Object.keys(pick).length

  return (
    <div
      className={`batchdrop${over ? ' is-over' : ''}`}
      onDragOver={(e) => {
        e.preventDefault()
        if (!blocked && !busy) setOver(true)
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setOver(false)
        if (busy) return
        void push(Array.from(e.dataTransfer.files))
      }}
    >
      {children}

      {/*
        只有当「有文件认不出」时才出现 —— 那是一个需要人做决定的状态，
        不做决定就只能在页面上干等着。平时它一个字都不占。
      */}
      {leftover.length ? (
        <div className="batchdrop__left">
          <div className="batchdrop__left-head">
            <Badge tone="warning">{leftover.length} 个文件认不出属于哪一行</Badge>
            <span className="text-xs muted">
              没猜 —— 请你指一下，指完再传（它们还没上传）
            </span>
            <span className="grow" />
            <ClearButton
              count={leftover.length}
              unit="个"
              title="清空未归位清单（这些还没传，图还在你电脑上）"
              onClear={() => {
                setLeftover([])
                setPick({})
              }}
            />
          </div>
          <ul className="batchdrop__left-list">
            {leftover.map((x) => (
              <li key={x.index}>
                <span className="batchdrop__name" title={x.name}>
                  {x.name}
                </span>
                <Select
                  aria-label={`把 ${x.name} 归到哪一行`}
                  value={pick[x.index] ? String(pick[x.index]) : ''}
                  onChange={(e) =>
                    setPick((m) => {
                      const next = { ...m }
                      if (e.target.value) next[x.index] = Number(e.target.value)
                      else delete next[x.index]
                      return next
                    })
                  }
                >
                  <option value="">归到…</option>
                  {rows.map((r) => (
                    <option key={r.line} value={r.line}>
                      {r.sku || r.productName || `第 ${r.line} 行`}
                    </option>
                  ))}
                </Select>
              </li>
            ))}
          </ul>
          <div className="batchdrop__foot">
            <Button
              variant="primary"
              size="sm"
              icon="check"
              disabled={busy || !pickedCount}
              onClick={() => void assignPicked()}
            >
              归位这 {pickedCount} 个
            </Button>
            <span className="text-xs muted">
              也可以把它们直接拖到对应那一行的「参考图」格里
            </span>
          </div>
        </div>
      ) : null}

      {/*
        归位进度 —— 只在拖进来之后才出现的一行字（它不是常驻控件）。
        没有它，用户面对一个转很久的圈唯一能做的就是刷新重来。
      */}
      {busy && progress ? (
        <p className="batchdrop__progress text-xs muted">
          <span className="spinner" /> 正在按文件名归位：已传 {progress.done} / {progress.total}
        </p>
      ) : null}
    </div>
  )
}
